import { type FC } from "react"

import Tooltip from "@forento/ui/components/Tooltip"

import Checkbox from "~/components/Checkbox"
import { ArrowIcon, EditIcon } from "~/components/Icon"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import Link from "~/components/Link"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import ListItem from "./ListItem"

const StudentEmailsSettingsPage: FC = () => {
	const user = useUser()
	const platform = usePlatform()!
	const toast = useToast()

	const { data: emails, error, refetch } = query.studentEmails.list.useQuery()

	const updateEmail = query.studentEmails.update.useMutation({
		async onSuccess() {
			await Promise.all([refetch(), user.reload()])
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to update email. Please try again.")
		},
	})

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title="Email Automation"
					path={[{ title: "Settings", link: routes.settings.index() }]}
				/>
			</PageHeader>
			{error ? (
				<p>Failed to load emails.</p>
			) : !emails ? (
				<PartialLoadingPage />
			) : (
				<div className="flex flex-col gap-4">
					<ListItem
						label="Product access"
						description="Sent when a student buys a product, is given access manually or through Zapier, or, within an hour, starts a free course, downloads a free downloadable or registers for a free event. A great chance to confirm key details and upsell free or entry-level users to your main offer."
						isLocked={platform.plan.maxProductAccessEmails === 0}
						actions={
							<Link to={routes.settings.studentEmail.productAccess()}>
								<ArrowIcon direction="right" className="size-6" />
							</Link>
						}
					/>
					<ListItem
						label="Website form replies"
						description="Sent to whoever submits a form on your website, the moment they do. Confirm you got their message, share next steps, or deliver what you promised in return for their email."
						isLocked={!platform.plan.access.landingPage}
						actions={
							<Link to={routes.settings.studentEmail.leadForm()}>
								<ArrowIcon direction="right" className="size-6" />
							</Link>
						}
					/>
					{emails.map(email => (
						<ListItem
							key={email.id}
							label={email.label}
							description={email.description}
							isLocked={
								(email.id === "chapter-dripped" && !platform.plan.access.chapterDripping) ||
								(email.id === "empty-student-created" && !platform.plan.access.zapier)
							}
							actions={
								<>
									<Tooltip tooltip="Edit email">
										<Link to={routes.settings.studentEmail.detail(email.id)}>
											<EditIcon className="size-6" />
										</Link>
									</Tooltip>
									<Tooltip tooltip={email.isRequired ? "This email is required" : "Enable email"}>
										<Checkbox
											ariaLabel={`Enable ${email.label}`}
											isChecked={email.isEnabled}
											onChange={isEnabled =>
												updateEmail.mutateAsync({ emailId: email.id, data: { isEnabled } })
											}
											isLoading={
												updateEmail.isPending && updateEmail.variables?.emailId === email.id
											}
											isDisabled={updateEmail.isPending || email.isRequired}
										/>
									</Tooltip>
								</>
							}
						/>
					))}
				</div>
			)}
		</Layout>
	)
}

export default StudentEmailsSettingsPage
